import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Message } from "primereact/message";
import { RadioButton } from "primereact/radiobutton";
import { Toast } from "primereact/toast";
import { RefObject, useEffect, useMemo, useState } from "react";

import {
  useCreateEdiSplitMutation,
  useGetAllEdiConversionFilesQuery,
} from "../../../store/queries/ediConversion";
import { useGetAllProvidersQuery } from "../../../store/queries/providers";
import {
  EdiSplitProcessMode,
  IEdiConversionFile,
  IEdiSplitBatch,
} from "../../../types/ediConversionFile";
import { canSplitEdiFile } from "./ediFileStatus";

export const DEFAULT_RECORDS_PER_PIECE = 2000;
export const MAX_RECORDS_PER_PIECE = 50000;

const PROCESS_MODE_OPTIONS: {
  value: EdiSplitProcessMode;
  label: string;
  hint: string;
}[] = [
  {
    value: "first",
    label: "Process the first piece, hold the rest",
    hint: "Piece 1 is processed right away. The others wait on hold until you process them.",
  },
  {
    value: "all",
    label: "Process all pieces",
    hint: "Every piece is processed as soon as the split finishes.",
  },
  {
    value: "none",
    label: "Hold all pieces",
    hint: "Nothing is processed until you start a piece yourself.",
  },
];

// Fallbacks for when the API's error carries no message of its own.
const ERROR_BY_STATUS: Record<number, string> = {
  400: "The split request was rejected.",
  404: "That file no longer exists.",
  409: "This file is busy — it is still processing or is already being split.",
};

interface ISplitEdiFileDialogProps {
  visible: boolean;
  onHide: () => void;
  // Pre-selects this file (the "Split into smaller files" row action on the files table).
  initialFile?: IEdiConversionFile | null;
  // The page's toast, so the outcome stays visible after the dialog closes.
  toast: RefObject<Toast>;
  // Called with the queued batch once the API has accepted the split.
  onSplitStarted?: (batch: IEdiSplitBatch) => void;
}

/**
 * CLE-2921 — "Split 835 file into smaller files" (SuperAdmin). Queues a split of one processed 835
 * conversion file into pieces of at most N records, cut at interchange boundaries; the pieces replace
 * the file. Only files that are fully processed and carry a provider are offered — anything else the
 * API refuses.
 */
function SplitEdiFileDialog({
  visible,
  onHide,
  initialFile,
  toast,
  onSplitStarted,
}: ISplitEdiFileDialogProps) {
  const [fileId, setFileId] = useState<string | null>(initialFile?.id ?? null);
  const [recordsPerPiece, setRecordsPerPiece] = useState<number | null>(
    DEFAULT_RECORDS_PER_PIECE
  );
  const [processMode, setProcessMode] = useState<EdiSplitProcessMode>("first");

  // Same arguments as the files table's unfiltered list, so this shares its cache entry.
  const { data: filesData, isLoading: filesLoading } =
    useGetAllEdiConversionFilesQuery({ search: "" }, { skip: !visible });
  const { data: providersData } = useGetAllProvidersQuery();
  const [createSplit, { isLoading: isSubmitting }] =
    useCreateEdiSplitMutation();

  // Start from the defaults every time the dialog opens.
  useEffect(() => {
    if (!visible) return;
    setFileId(initialFile?.id ?? null);
    setRecordsPerPiece(DEFAULT_RECORDS_PER_PIECE);
    setProcessMode("first");
  }, [visible, initialFile]);

  const files = useMemo(() => {
    const splittable = (filesData?.data ?? []).filter(canSplitEdiFile);
    // The row action's file stays selectable while the list is still loading.
    if (
      initialFile &&
      canSplitEdiFile(initialFile) &&
      !splittable.some((f) => f.id === initialFile.id)
    ) {
      return [initialFile, ...splittable];
    }
    return splittable;
  }, [filesData, initialFile]);

  const providerName = (providerId?: string) =>
    providersData?.data?.find((p) => p.id === providerId)?.name ??
    "Unknown provider";

  const fileOptions = files.map((f) => ({
    value: f.id,
    label: `${f.displayName} · ${providerName(
      f.providerId
    )} · ${f.recordCount.toLocaleString()} records`,
  }));

  const selected = files.find((f) => f.id === fileId) ?? null;
  // The max as a usable number, or null while the field is empty or out of range.
  const maxRecords =
    recordsPerPiece !== null &&
    Number.isInteger(recordsPerPiece) &&
    recordsPerPiece >= 1 &&
    recordsPerPiece <= MAX_RECORDS_PER_PIECE
      ? recordsPerPiece
      : null;
  const nothingToSplit =
    !!selected && maxRecords !== null && selected.recordCount <= maxRecords;
  const estimatedPieces =
    selected && maxRecords !== null && selected.recordCount > maxRecords
      ? Math.ceil(selected.recordCount / maxRecords)
      : null;
  const canSubmit =
    !!selected && maxRecords !== null && !nothingToSplit && !isSubmitting;

  const handleSubmit = async () => {
    if (!selected || !canSubmit || maxRecords === null) return;
    try {
      const result = await createSplit({
        sourceEdiConversionFileId: selected.id,
        recordsPerPiece: maxRecords,
        processMode,
      }).unwrap();
      toast.current?.show({
        severity: "success",
        summary: "Split started",
        detail: "Watch progress in the Split 835 Files tab.",
      });
      onHide();
      onSplitStarted?.(result.data);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Could not start the split",
        detail:
          error?.data?.message ||
          error?.data?.messages?.[0] ||
          ERROR_BY_STATUS[error?.status] ||
          "Failed to start the split.",
        life: 8000,
      });
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        disabled={isSubmitting}
      />
      <Button
        label="Split"
        icon="pi pi-sitemap"
        onClick={handleSubmit}
        disabled={!canSubmit}
        loading={isSubmitting}
      />
    </div>
  );

  return (
    <Dialog
      header="Split 835 file into smaller files"
      visible={visible}
      onHide={isSubmitting ? () => {} : onHide}
      closable={!isSubmitting}
      style={{ width: "min(40rem, 95vw)" }}
      footer={footer}
      blockScroll
    >
      <div className="mb-4">
        <label htmlFor="split-file" className="block font-medium mb-2">
          File to split
        </label>
        <Dropdown
          inputId="split-file"
          value={fileId}
          options={fileOptions}
          onChange={(e) => setFileId(e.value ?? null)}
          placeholder={
            filesLoading ? "Loading files…" : "Select a processed 835 file…"
          }
          filter
          filterBy="label"
          disabled={isSubmitting}
          emptyMessage="No processed files with a provider."
          className="w-full"
        />
      </div>

      <div className="mb-4">
        <label
          htmlFor="split-records-per-piece"
          className="block font-medium mb-2"
        >
          Max records per piece
        </label>
        <InputNumber
          inputId="split-records-per-piece"
          value={recordsPerPiece}
          onValueChange={(e) => setRecordsPerPiece(e.value ?? null)}
          min={1}
          max={MAX_RECORDS_PER_PIECE}
          maxFractionDigits={0}
          useGrouping
          disabled={isSubmitting}
        />
        {estimatedPieces !== null && (
          <small className="block text-600 mt-2">
            About {estimatedPieces.toLocaleString()} pieces
          </small>
        )}
        {maxRecords === null && (
          <small className="p-error block mt-2">
            Enter a whole number from 1 to{" "}
            {MAX_RECORDS_PER_PIECE.toLocaleString()}.
          </small>
        )}
        {nothingToSplit && selected && maxRecords !== null && (
          <Message
            severity="warn"
            className="w-full justify-content-start mt-2"
            text={`This file already has ${maxRecords.toLocaleString()} records or fewer (${selected.recordCount.toLocaleString()}) — nothing to split.`}
          />
        )}
      </div>

      <div className="mb-4">
        <span className="block font-medium mb-2">After splitting</span>
        {PROCESS_MODE_OPTIONS.map((option) => (
          <div key={option.value} className="flex align-items-start mb-2">
            <RadioButton
              inputId={`split-mode-${option.value}`}
              name="split-process-mode"
              value={option.value}
              checked={processMode === option.value}
              onChange={(e) => setProcessMode(e.value)}
              disabled={isSubmitting}
            />
            <div className="ml-2">
              <label
                htmlFor={`split-mode-${option.value}`}
                className="cursor-pointer"
              >
                {option.label}
              </label>
              <small className="block text-600">{option.hint}</small>
            </div>
          </div>
        ))}
      </div>

      <Message
        severity="info"
        className="w-full justify-content-start"
        text="Pieces are cut at interchange boundaries, so a piece can land a little under the maximum. The original file is removed and its claims are deleted; they come back as each piece is processed. Other slices from the same upload are not touched."
      />
    </Dialog>
  );
}

export default SplitEdiFileDialog;
